// Copy, defaults and sources for /guides/3d-printing-cost-calculator/.
// Every number comes from a cited source: filament prices, electricity and
// printer wattage from the printer cost guide's ledger, the 100 g / 5 h
// worked example from the 3D printing cost guide, and PLA density from
// Bambu Lab's data sheet. Studio prices are the published ones only.
const {
  electricity,
  powerReadings,
  ownerSources,
} = require('./printer-cost-guide')
const {
  homeExample,
  homeExampleCosts,
  printCostGuide,
} = require('./print-cost-guide')

const printCostCalculator = {
  path: '/guides/3d-printing-cost-calculator/',
  title: '3D Printing Cost Calculator',
  cardTitle: '3D printing cost calculator (free, STL)',
  metaTitle: '3D Printing Cost Calculator: Free STL Price Estimate',
  description:
    'Free 3D printing cost calculator. Drop in an STL to see its weight, filament and electricity cost. Your file stays on your device.',
  reviewedDate: '2026-11-03',
  reviewedDateLabel: 'November 3, 2026',
}

const sources = {
  density: {
    label: 'Bambu Lab PLA Basic technical data sheet',
    detail: 'density 1.24 g/cm³ (ISO 1183)',
    url: 'https://cdn.shopify.com/s/files/1/0380/5149/files/Bambu_PLA_Basic_Technical_Data_Sheet.pdf',
  },
  filament: {
    label: 'Bambu Lab and Prusa brand stores, checked October 27, 2026',
    detail: `PLA $${homeExample.plaPerKg.low}–$${homeExample.plaPerKg.high} per 1 kg spool`,
    url: printCostGuide.path,
  },
  electricity: {
    label: `U.S. EIA, ${electricity.month} residential average`,
    detail: `${electricity.centsPerKwh}¢ per kWh`,
    url: electricity.source,
  },
  speed: {
    label: 'Our 3D printing cost guide’s worked example',
    detail: `${homeExample.grams} g in ${homeExample.hours} hours`,
    url: printCostGuide.path,
  },
}

// Owner-measured wattage while printing (printer cost guide). The A1 reading
// is the 110 W midpoint the cost guide's worked example uses.
const printers = [
  {
    id: 'a1',
    label: 'Bambu Lab A1',
    watts: 110,
    source: ownerSources[powerReadings[0].source],
  },
  {
    id: 'bambu',
    label: 'Bambu Lab (owner’s fleet)',
    watts: 103.6,
    source: ownerSources[powerReadings[1].source],
  },
  {
    id: 'k2',
    label: 'Creality K2 Plus',
    watts: 190,
    source: ownerSources[powerReadings[2].source],
  },
]

const defaults = {
  density: 1.24, // g/cm³, PLA
  pricePerKg: homeExample.plaPerKg, // { low, high }
  centsPerKwh: electricity.centsPerKwh,
  gramsPerHour: homeExample.grams / homeExample.hours,
  printer: 'a1',
  // Slicer-style settings. These are the user's choices, not facts.
  infill: 15,
  wallMm: 0.8, // two 0.4 mm perimeters
}

const infillOptions = [10, 15, 20, 40, 100]

const units = [
  { id: 'mm', label: 'mm', factor: 1 },
  { id: 'cm', label: 'cm', factor: 10 },
  { id: 'in', label: 'inches', factor: 25.4 },
]

const studio = {
  printFrom: 30,
  modelingFrom: 250,
  servicePath: '/services/3d-printing/',
  typeformUrl: 'https://printaworld.typeform.com/to/Xf4KYP',
}

const method = [
  {
    label: '01',
    title: 'Measure the model',
    body: 'Your browser reads the STL’s triangles and adds up the enclosed volume and the surface area. Nothing is uploaded.',
  },
  {
    label: '02',
    title: 'Estimate the weight',
    body: `The outer walls are printed solid and the inside at your infill percentage. Weight is that plastic volume times PLA’s density, ${defaults.density} g/cm³.`,
  },
  {
    label: '03',
    title: 'Price the filament',
    body: `Weight times the price of a 1 kg spool: $${homeExample.plaPerKg.low} (Bambu Lab PLA Basic on sale) to $${homeExample.plaPerKg.high} (Prusament PLA). Enter your own spool price if you know it.`,
  },
  {
    label: '04',
    title: 'Add the electricity',
    body: `Print hours times the printer’s measured watts times ${electricity.centsPerKwh}¢ per kWh. Without your slicer’s time, hours assume ${defaults.gramsPerHour} g per hour.`,
  },
]

const leftOut = [
  'Supports, brims and purge waste, which add plastic your slicer will show',
  'The printer itself, its wear and replacement parts',
  'Failed prints and test prints',
  'Sanding, painting and other finishing',
  'Your time to prepare, print and clean up the part',
]

const faqs = [
  {
    question: 'How do you calculate the cost of a 3D print?',
    answer: `Multiply the print’s weight by your filament’s price per gram, then add the electricity: print hours × the printer’s watts ÷ 1,000 × your price per kWh. A ${homeExample.grams} g PLA print that takes ${homeExample.hours} hours on a 110 W printer costs about $${homeExampleCosts.totalLow}–$${homeExampleCosts.totalHigh} at the U.S. average of ${electricity.centsPerKwh}¢ per kWh.`,
  },
  {
    question: 'Is my STL file uploaded anywhere?',
    answer:
      'No. The calculator reads the file in your browser, on your own device. It isn’t sent to PrintAWorld or anyone else, and it’s gone when you close the page.',
  },
  {
    question: 'Why is the estimate different from my slicer?',
    answer:
      'Slicers know your exact walls, top and bottom layers, infill pattern, supports and speed. The calculator approximates those. For the closest number, type the grams and print time from your slicer into the calculator.',
  },
  {
    question: 'How much does it cost to 3D print something at home?',
    answer: `Once you own a printer, material and power are cheap: about $${homeExampleCosts.totalLow}–$${homeExampleCosts.totalHigh} for a ${homeExample.grams} g PLA print. The printer, upkeep, failed prints and your time cost more. See how much a 3D printer costs for the up-front side.`,
  },
  {
    question: 'Does the calculator work for resin prints?',
    answer:
      'Roughly. The weight uses PLA’s density and the electricity uses filament printers’ measured watts. For resin, enter your resin’s price per kilogram as the spool price and treat the result as a ballpark.',
  },
  {
    question: 'How much does it cost to have something 3D printed?',
    answer: `PrintAWorld prints for businesses, brands and production teams. A simple print from your file starts around $${studio.printFrom}, with no setup fee and no minimum order; production runs on our in-house FDM print farm and finished or very large pieces are quoted per project. 3D modeling starts at $${studio.modelingFrom} if you don’t have a file. For a hobby print, a home printer or a print marketplace usually costs less.`,
  },
]

module.exports = {
  printCostCalculator,
  sources,
  printers,
  defaults,
  infillOptions,
  units,
  studio,
  method,
  leftOut,
  faqs,
}
